import type { IBoondock, ILandOwner, INoise, IPrivacy, ISite, IUser } from '@curbnturf/objects';

export namespace boondockComplete {
  const missingAddress = (contact: IUser | ILandOwner): boolean => {
    return (
      !contact ||
      !contact.firstName ||
      !contact.lastName ||
      !contact.address ||
      !contact.address.address ||
      !contact.address.city ||
      !contact.address.state ||
      !contact.phone
    );
  };

  const testDescriptionText = (boondock: IBoondock): { messages: string[]; completeness: number } => {
    if (boondock && boondock.description && boondock.description.length >= 150) {
      return { messages: [], completeness: 4 };
    } else {
      return { messages: ['Missing Description Text'], completeness: 0 };
    }
  };

  const testDirections = (site: ISite): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!site) {
      return { messages: ['Missing Directions'], completeness };
    }

    if (site.coordinates) {
      if (site.coordinates.lat) {
        completeness += 2;
      } else {
        messages.push('Missing Latitude Coordinates');
      }

      if (site.coordinates.lon) {
        completeness += 2;
      } else {
        messages.push('Missing Longitude Coordinates');
      }
    }

    return { messages, completeness };
  };

  const testLengthOfStay = (boondock: IBoondock): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!boondock) {
      return { messages: ['Missing Minimum Length of Stay', 'Missing Maximum Length of Stay'], completeness };
    }

    if (boondock.maxLengthOfStayInDays) {
      completeness += 2;
    } else {
      messages.push('Missing Maximum Length of Stay');
    }

    return { messages, completeness };
  };

  const testBooking = (boondock: IBoondock): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!boondock) {
      return { messages, completeness };
    }

    const lengthOfStay = testLengthOfStay(boondock);
    completeness += lengthOfStay.completeness;
    messages.push(...lengthOfStay.messages);

    return { messages, completeness };
  };

  const testLandOwner = (boondock: IBoondock): { messages: string[]; completeness: number } => {
    const messages: string[] = [];
    const landOwner = boondock.landOwner;
    if (!landOwner) {
      return { messages: ['Missing Land Owner'], completeness: 0 };
    }

    if (landOwner.self) {
      return { messages: [], completeness: 2 };
    }

    if (landOwner.lastName) {
      if (landOwner.private) {
        if (!missingAddress(landOwner)) {
          return { messages: [], completeness: 2 };
        } else {
          messages.push('Missing Land Owner Address');
        }
      } else {
        return { messages: [], completeness: 2 };
      }
    }

    return { messages: [...messages, 'Missing Complete Land Owner Information'], completeness: 0 };
  };

  const testName = (boondock: IBoondock): { messages: string[]; completeness: number } => {
    if (boondock && boondock.name && boondock.name.length >= 8) {
      return { messages: [], completeness: 2 };
    } else {
      return { messages: ['Missing Site Name'], completeness: 0 };
    }
  };

  const testPhotos = (_boondock: IBoondock): { messages: string[]; completeness: number } => {
    // for now boondocks don't require photos
    return { messages: [], completeness: 6 };
  };

  const testNoise = (noise?: INoise): { messages: string[]; completeness: number } => {
    let returnValue = 0;
    const messages: string[] = [];

    if (noise && noise.level) {
      returnValue += 4;
    } else {
      messages.push('Missing Noise Selection');
    }

    return { messages, completeness: returnValue };
  };

  const testPrivacy = (privacy?: IPrivacy): { messages: string[]; completeness: number } => {
    const messages: string[] = [];
    if (privacy && (privacy.home || privacy.neighbor || privacy.others || privacy.secluded)) {
      return { messages, completeness: 4 };
    }

    messages.push('Missing Privacy Selection.');
    return { messages, completeness: 0 };
  };

  const testTerrain = (boondock: IBoondock): { messages: string[]; completeness: number } => {
    if (boondock.landTypes && boondock.landTypes.length > 0) {
      return { messages: [], completeness: 4 };
    } else {
      return { messages: ['Missing Terrain Information'], completeness: 0 };
    }
  };

  const testDescription = (boondock: IBoondock): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!boondock) {
      return { messages: ['Missing Boondock'], completeness };
    }

    const name = testName(boondock);
    completeness += name.completeness;
    messages.push(...name.messages);

    const descriptionTest = testDescriptionText(boondock);
    completeness += descriptionTest.completeness;
    messages.push(...descriptionTest.messages);

    const landOwner = testLandOwner(boondock);
    completeness += landOwner.completeness;
    messages.push(...landOwner.messages);

    const terrain = testTerrain(boondock);
    completeness += terrain.completeness;
    messages.push(...terrain.messages);

    const photos = testPhotos(boondock);
    completeness += photos.completeness;
    messages.push(...photos.messages);

    return { messages, completeness };
  };

  const testOverview = (boondock: IBoondock): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!boondock) {
      return { messages, completeness };
    }

    // on site activities
    completeness += 4;

    // off site activities
    completeness += 2;

    // amenities
    completeness += 8;

    // vehicles
    completeness += 4;

    const noise = testNoise(boondock.noise);
    completeness += noise.completeness;
    messages.push(...noise.messages);

    const privacy = testPrivacy(boondock.privacy);
    completeness += privacy.completeness;
    messages.push(...privacy.messages);

    return { messages, completeness };
  };

  const testUserName = (user: IUser): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['Missing User Profile'], completeness };
    }

    if (user.firstName) {
      completeness += 2;
    } else {
      messages.push('Missing Profile First Name');
    }

    if (user.lastName) {
      completeness += 2;
    } else {
      messages.push('Missing Profile Last Name');
    }

    return { messages, completeness };
  };

  const testUserPhoto = (user: IUser): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['Missing User Profile'], completeness };
    }

    if (user.photo?.key) {
      completeness += 4;
    } else {
      messages.push('Missing User Profile Photo');
    }

    return { messages, completeness };
  };

  const testUserPhone = (user: IUser): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['Missing User Profile'], completeness };
    }

    if (user.phone) {
      completeness += 4;
    } else {
      messages.push('Missing Profile Phone Number');
    }

    return { messages, completeness };
  };

  export const userProfilePercentComplete = (user?: IUser): { messages: string[]; completeness: number } => {
    let completeness = 4;
    const messages: string[] = [];

    if (!user) {
      return { messages: ['User Profile Missing'], completeness };
    }

    const userName = testUserName(user);
    completeness += userName.completeness;
    messages.push(...userName.messages);

    const userPhoto = testUserPhoto(user);
    completeness += userPhoto.completeness;
    messages.push(...userPhoto.messages);

    const userPhone = testUserPhone(user);
    completeness += userPhone.completeness;
    messages.push(...userPhone.messages);

    return { messages, completeness };
  };

  export const overviewCompleted = (boondock: IBoondock): boolean => {
    const completeness = testOverview(boondock).completeness;

    return completeness >= 26; // 4 + 2 + 12 + 4
  };

  export const descriptionTextCompleted = (boondock: IBoondock): boolean => {
    const completeness = testDescriptionText(boondock).completeness;

    return completeness >= 4;
  };

  export const directionsCompleted = (boondock: IBoondock): boolean => {
    const completeness = testDirections(boondock).completeness;

    return completeness >= 4;
  };

  export const landOwnerCompleted = (boondock: IBoondock): boolean => {
    const completeness = testLandOwner(boondock).completeness;

    return completeness >= 2;
  };

  export const lengthOfStayCompleted = (boondock: IBoondock): boolean => {
    const completeness = testLengthOfStay(boondock).completeness;

    return completeness >= 4;
  };

  export const nameCompleted = (boondock: IBoondock): boolean => {
    const completeness = testName(boondock).completeness;

    return completeness >= 2;
  };

  export const photosCompleted = (boondock: IBoondock): boolean => {
    const completeness = testPhotos(boondock).completeness;

    return completeness >= 6;
  };

  export const bookingCompleted = (boondock: IBoondock): boolean => {
    return lengthOfStayCompleted(boondock);
  };

  export const terrainCompleted = (boondock: IBoondock): boolean => {
    const completeness = testTerrain(boondock).completeness;
    return completeness >= 4;
  };

  export const descriptionCompleted = (boondock: IBoondock): boolean => {
    return (
      nameCompleted(boondock) &&
      descriptionTextCompleted(boondock) &&
      landOwnerCompleted(boondock) &&
      terrainCompleted(boondock) &&
      photosCompleted(boondock)
    );
  };

  export const userNameCompleted = (user: IUser): boolean => {
    return testUserName(user).completeness >= 4;
  };

  export const userPhotoCompleted = (user: IUser): boolean => {
    return testUserPhoto(user).completeness >= 4;
  };

  export const userPhoneCompleted = (user: IUser): boolean => {
    return testUserPhone(user).completeness >= 4;
  };

  export const userProfileCompleted = (user: IUser): boolean => {
    return userNameCompleted(user) && userPhotoCompleted(user) && userPhoneCompleted(user);
  };

  export const userCompleted = (user: IUser): boolean => {
    return userProfileCompleted(user) && userPhotoCompleted(user);
  };

  export const boondockPercentComplete = (boondock: IBoondock): { messages: string[]; completeness: number } => {
    let completeness = 0;
    const messages: string[] = [];

    if (!boondock) {
      return { messages: ['Missing Site'], completeness };
    }

    const description = testDescription(boondock);
    completeness += description.completeness;
    messages.push(...description.messages);

    const directions = testDirections(boondock);
    completeness += directions.completeness;
    messages.push(...directions.messages);

    const profilePercentComplete = userProfilePercentComplete(boondock.user);
    completeness += profilePercentComplete.completeness;
    messages.push(...profilePercentComplete.messages);

    const booking = testBooking(boondock);
    completeness += booking.completeness;
    messages.push(...booking.messages);

    return { messages, completeness: Math.floor((completeness / 103) * 100) };
  };

  export const completed = (boondock: IBoondock): boolean => {
    return boondockPercentComplete(boondock).completeness >= 100;
  };

  export const boondockCompleteMessages = (boondock: IBoondock): string[] => {
    const messages: string[] = [];

    if (!boondock) {
      return ['Missing Site'];
    }

    messages.push(...testDescription(boondock).messages);
    messages.push(...testDirections(boondock).messages);
    messages.push(...userProfilePercentComplete(boondock.user).messages);

    messages.push(...testBooking(boondock).messages);

    return messages;
  };
}
